import { LocalizedText } from "@/i18n/LocalizedText";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Info } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { Skeleton } from "./ui/skeleton";
import { Spinner } from "./ui/spinner";
import noData from "../assets/no-data.svg";
import type { JSX, ReactNode } from "react";
import { useNavigate } from "react-router";
import { Fragment, useMemo } from "react";
import { canonicalizeDomain } from "@/utils/domain";

type LoadingStateCopy = {
  title: ReactNode;
  subtitle: ReactNode;
};

type CompetitorBanner = {
  title: ReactNode;
  body: ReactNode;
  buttonLabel: ReactNode;
  onClick: () => void;
};

type CurrentBrandRef = {
  brandId?: string;
  id?: string;
  url?: string | null;
};

export const SimpleTable = ({
  data,
  isBlured,
  isRunResultLoading = false,
  title = "",
  isOnlyYou = false,
  currentBrand,
  description = "",
  mainColumnName = "Domain",
  lastColumn = "Visibility",
  middleColumn,
  action = null,
  tooltip = null,
  maxTableHeight,
  loadingStateCopy,
  competitorBanner,
  hidden = false,
  onAddCompetitorClick,
}: {
  data: any[];
  isRunResultLoading?: boolean;
  title?: ReactNode;
  currentBrand?: CurrentBrandRef | null;
  isOnlyYou?: boolean;
  mainColumnName?: ReactNode;
  isBlured?: any[] | null;
  description?: ReactNode;
  lastColumn?: ReactNode;
  middleColumn?: ReactNode;
  tooltip?: { text: ReactNode; icon: JSX.Element } | null;
  action?: (() => void) | null;
  maxTableHeight?: number | string;
  loadingStateCopy?: LoadingStateCopy | null;
  competitorBanner?: CompetitorBanner | null;
  onAddCompetitorClick?: (() => void) | null;
  hidden?: boolean;
}) => {
  const navigate = useNavigate();
  const currentBrandDomain = useMemo(
    () => canonicalizeDomain(currentBrand?.url),
    [currentBrand?.url],
  );
  const brandSearchValue = currentBrand?.brandId || currentBrand?.id;
  const brandSearch = brandSearchValue ? `?brandId=${brandSearchValue}` : "";
  const columnCount = middleColumn ? 4 : 3;

  return (
    <Card className={`flex-1 gap-2 ${hidden && "border-none py-0"}`}>
      <CardHeader className={`flex justify-between ${hidden && "hidden"}`}>
        <div className="w-full flex flex-col gap-1">
          <CardTitle className="min-h-[36px] flex items-center justify-between">
            <div className="flex items-center gap-2">
              {title}
              {tooltip && (
                <Tooltip>
                  <TooltipTrigger asChild>{tooltip.icon}</TooltipTrigger>
                  <TooltipContent className="py-2">
                    {tooltip.text}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            {action && (
              <Button
                className="cursor-pointer"
                variant="outline"
                onClick={action}
              >
                <LocalizedText id="legacy.show.all.33ncpz" />
              </Button>
            )}
          </CardTitle>
          {description && (
            <CardDescription>
              <span className="@[540px]/card:block">{description}</span>
            </CardDescription>
          )}
        </div>
      </CardHeader>
      <CardContent className={`h-full ${hidden && "px-0"}`}>
        <div
          className={maxTableHeight ? "overflow-y-auto" : undefined}
          style={maxTableHeight ? { maxHeight: maxTableHeight } : undefined}
        >
          <Table className="h-full">
            <TableHeader className="">
              <TableRow className="text-sm bg-[#f5f5f5] hover:bg-[#f5f5f5]">
                <TableHead className="h-[34px] w-12 font-normal">#</TableHead>
                <TableHead className="h-[34px] font-normal">
                  {mainColumnName}
                </TableHead>
                {middleColumn && (
                  <TableHead className="h-[34px] text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            <LocalizedText id="legacy.average.position.compared.to.selected.competitors.across.ai.1nm5mh3" />
                          </p>
                        </TooltipContent>
                      </Tooltip>
                      <span className="font-normal">{middleColumn}</span>
                    </div>
                  </TableHead>
                )}
                <TableHead className="h-[34px] w-[30px] text-right">
                  <div className="flex items-center justify-end gap-1 w-fit">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          {" "}
                          <LocalizedText id="legacy.percentage.of.tracked.prompts.where.the.brand.appears.86xnm2" />
                        </p>
                      </TooltipContent>
                    </Tooltip>
                    <span className="font-normal">{lastColumn}</span>
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            {isRunResultLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    className="h-24 text-center relative whitespace-normal"
                  >
                    <div className="relative flex flex-col gap-2 overflow-hidden">
                      {loadingStateCopy ? (
                        <div className="mb-4 flex flex-col items-center justify-center gap-1 text-center">
                          <p className="text-base font-semibold leading-6 text-[#0A0A0A]">
                            {loadingStateCopy.title}
                          </p>
                          <p className="max-w-[360px] text-sm leading-5 text-[#737373]">
                            {loadingStateCopy.subtitle}
                          </p>
                        </div>
                      ) : (
                        <div className="z-10 gap-1 flex-col absolute flex justify-center items-center w-full h-full">
                          <div className="flex gap-2 items-center flex-col p-2 bg-muted rounded-md">
                            <Spinner
                              className="text-[#FF5E00]"
                              variant="circle"
                            ></Spinner>
                          </div>
                          <div>
                            <LocalizedText id="legacy.loading.results.18j17b" />
                          </div>
                        </div>
                      )}
                      {Array.from({ length: 6 }).map((_, index) => {
                        return (
                          <div key={index} className="flex w-full relative">
                            <div className="flex items-center gap-2 w-full min-w-0">
                              <Skeleton className="h-[20px] w-[25px] rounded-full shrink-0" />
                              <div className="flex min-w-0 flex-1 gap-2">
                                <Skeleton className="h-4 flex-[3]" />
                                <Skeleton className="h-4 flex-1" />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {data.length ? (
                  data.map((item, index: number) => {
                    const rowIsCurrentBrand =
                      Boolean(item?.isOwnBrand) ||
                      item.domain === currentBrandDomain;
                    return (
                      <Fragment key={index}>
                        <TableRow
                          className={item.rank === 18 ? "bg-muted" : ""}
                        >
                          <TableCell
                            className={`${rowIsCurrentBrand && "font-bold"} ${isBlured && "blur-xs select-none pointer-events-none"} font-medium`}
                          >
                            {index + 1}
                          </TableCell>
                          <TableCell
                            className={`flex gap-2 items-center ${isBlured && "blur-xs select-none pointer-events-none"}`}
                          >
                            {(() => {
                              const faviconDomain =
                                item.faviconDomain ||
                                item.domainUrl ||
                                item.domain;
                              const formattedFavicon =
                                typeof faviconDomain === "string" &&
                                faviconDomain.length
                                  ? faviconDomain.startsWith("http")
                                    ? faviconDomain
                                    : `http://${faviconDomain}`
                                  : "";
                              return (
                                <img
                                  className="w-[24px] h-[24px]"
                                  src={
                                    formattedFavicon
                                      ? `https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${formattedFavicon}&size=64`
                                      : ""
                                  }
                                />
                              );
                            })()}
                            <p
                              className={`${rowIsCurrentBrand && "font-semibold"}`}
                            >
                              {" "}
                              {item.domain}
                            </p>
                          </TableCell>
                          {middleColumn && (
                            <TableCell
                              className={`${isBlured && "blur-xs select-none pointer-events-none"} ${rowIsCurrentBrand && "font-semibold"} text-right`}
                            >
                              {(() => {
                                if (
                                  item.position === null ||
                                  item.position === undefined
                                ) {
                                  return "-";
                                }
                                const rawPosition = Number(item.position);
                                if (
                                  !Number.isFinite(rawPosition) ||
                                  rawPosition <= 0
                                ) {
                                  return "-";
                                }
                                const displayValue = rawPosition
                                  .toFixed(1)
                                  .replace(/\.0$/, "");
                                return (
                                  <span className="flex items-center justify-end leading-5">
                                    <span
                                      className="text-sm text-[#737373] font-normal leading-5"
                                      style={{ fontFamily: "Inter" }}
                                    >
                                      #
                                    </span>
                                    <span className="ml-1">{displayValue}</span>
                                  </span>
                                );
                              })()}
                            </TableCell>
                          )}
                          <TableCell
                            className={`${isBlured && "blur-xs select-none pointer-events-none"} ${rowIsCurrentBrand && "font-semibold"} text-right`}
                          >
                            {item.points}
                          </TableCell>
                        </TableRow>
                        {isOnlyYou && (
                          <TableRow>
                            <TableCell
                              colSpan={columnCount}
                              className="text-right"
                            >
                              <div className="flex flex-col gap-2 items-center my-5">
                                <div className="text-lg text-foreground">
                                  <LocalizedText id="legacy.no.active.competitors.added.yet.11v4axf" />
                                </div>
                                <div className="text-sm text-center text-muted-foreground max-w-[288px] whitespace-break-spaces">
                                  <LocalizedText id="legacy.add.other.brands.to.see.how.your.visibility.5oxwmr" />
                                </div>
                                <Button
                                  type="submit"
                                  onClick={() => {
                                    if (onAddCompetitorClick) {
                                      onAddCompetitorClick();
                                      return;
                                    }
                                    navigate(
                                      `/dashboard/competitors${brandSearch}`,
                                    );
                                  }}
                                  className="cursor-pointer hover:bg-[#ff8138] text-white cursor-pointer bg-[#FF5E00]"
                                >
                                  <LocalizedText id="legacy.add.competitor.dmnwsk" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={columnCount} className="text-right">
                      <div className="flex flex-col gap-2 items-center my-5">
                        <div>
                          <img src={noData}></img>
                        </div>
                        <div className="text-lg text-foreground">
                          <LocalizedText id="legacy.no.data.1dp9hn8" />
                        </div>
                        <div className="text-sm text-muted-foreground max-w-[288px] whitespace-break-spaces">
                          <LocalizedText id="legacy.we.havent.received.any.data.15ocybb" />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            )}
          </Table>
        </div>
        {competitorBanner ? (
          <div className="mt-4 flex w-full items-center justify-between gap-3 rounded-[10px] bg-[#F5F5F5] p-3 text-left">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#0A0A0A]">
                {competitorBanner.title}
              </p>
              <p className="text-xs text-[#404040]">{competitorBanner.body}</p>
            </div>
            <Button
              type="button"
              className="shrink-0 cursor-pointer bg-[#FF5E00] text-white hover:bg-[#ff8138]"
              onClick={() => {
                competitorBanner.onClick();
              }}
            >
              {competitorBanner.buttonLabel}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
